/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react'
import {
    Accordion,
    AccordionItem,
    AccordionItemButton,
    AccordionItemHeading,
    AccordionItemPanel,
} from 'react-accessible-accordion'
import 'react-accessible-accordion/dist/fancy-example.css'
import ArrowIcon from '../../../assets/arrowIcon.png'
import ArrowUpIcon from '../../../assets/arrowUpIcon.png'
import IconDots from '../../../assets/dots.png'
import { RangeCriteriaDetail } from 'components/atoms/RangeCriteriaDetail/RangeCriteriaDetail'
import { MultiSelectCriteriaDetail } from 'components/atoms/MultiSelectCriteriaDetail/MultiSelectCriteriaDetail'
import { GeolocationCriteriaDetail } from 'components/atoms/GeolocationCriteriaDetail/GeolocationCriteriaDetail'
import { InputFileDetail } from 'components/atoms/InputFileDetail/InputFileDetail'
import { SwitchCriteriaDetail } from 'components/atoms/SwitchCriteriaDetail/SwitchCriteriaDetail'
import { MeasurementCriteriaDetail } from 'components/atoms/MeasurementCriteriaDetail/MeasurementCriteriaDetail'
import { CriteriaIcon } from 'components/atoms/CriteriaIcon/CriteriaIcon'
import { ButtonDropdown } from 'components/molecules/ButtonDropdown/ButtonDropdown'
import IconTrash from '../../../assets/trash.png'
import AppModalConfirmation from 'components/atoms/AppModalConfirmation/AppModalConfirmation'
import { Rule } from 'interfaces/rule'
import IconPencil from '../../../assets/pencil.png'
import { usePermissions } from 'hooks/usePermissions'
import { RolePermissions } from 'utils/constants'

interface Props {
    rules: {
        id: number
        chutes: number[]
        name: string
        criterias: any[]
        warehouse_name?: string
    }[]
    deleteRule: (rule: Rule) => void
    onEdit: (ruleId: number) => void
}

const RuleKeys = {
    weight: 'weight',
    declared_value: 'declared_value',
    postal_code_range: 'postal_code_range',
    distribution_provider: 'distribution_provider',
    service_code: 'service_code',
    service_types: 'service_types',
    clients: 'clients',
    states: 'states',
    town: 'town',
    township: 'township',
    postal_code_list: 'postal_code_list',
    ddu_ddp: 'ddu_ddp',
    measurements: 'measurements',
}
const SORT_PRIORITY = {
    [RuleKeys.postal_code_range]: 1,
    [RuleKeys.states]: 2,
    [RuleKeys.town]: 3,
    [RuleKeys.township]: 4,
}

export const AccordionRuleList = ({ rules, deleteRule, onEdit }: Props) => {
    const [expandedItems, setExpandedItems] = useState<string[]>([])
    const [confirmModal, setConfirmModal] = useState(false)
    const [ruleToDelete, setRuleToDelete] = useState<any>(null)
    const { can } = usePermissions()

    const canEdit = can(RolePermissions.rules.edit)
    const canDelete = can(RolePermissions.rules.delete)
    const hasDropdownOptions = canEdit || canDelete
    const sortCriteria = (criterias: any[]) => {
        return [...criterias].sort((a, b) => {
            const priorityA = SORT_PRIORITY[a.key] || 0
            const priorityB = SORT_PRIORITY[b.key] || 0
            return priorityA - priorityB
        })
    }

    const renderCriteria = (rule: any) => {
        switch (rule.key) {
            case RuleKeys.weight:
            case RuleKeys.declared_value:
            case RuleKeys.postal_code_range:
                return (
                    <RangeCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        valueKey={rule.key}
                        criteria_configurations={rule.criteria_configurations}
                    />
                )
            case RuleKeys.distribution_provider:
            case RuleKeys.service_code:
            case RuleKeys.service_types:
            case RuleKeys.clients:
                return (
                    <MultiSelectCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        valueKey={rule.key}
                        criteria_list={rule.criterial_list}
                    />
                )
            case RuleKeys.township:
            case RuleKeys.town:
            case RuleKeys.states:
                return (
                    <GeolocationCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        valueKey={rule.key}
                        criteria_list={rule.criterial_list}
                    />
                )
            case RuleKeys.postal_code_list:
                return (
                    <InputFileDetail
                        id={rule.id}
                        name={rule.name}
                        valueKey={rule.key}
                        criteria_quantity={rule.criterial_quantity}
                    />
                )
            case RuleKeys.ddu_ddp:
                return (
                    <SwitchCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        criteria_configurations={rule.criteria_configurations}
                        key={rule.key}
                    />
                )
            case RuleKeys.measurements:
                return (
                    <MeasurementCriteriaDetail
                        id={rule.id}
                        name={rule.name}
                        key={rule.valueKey}
                        criteria_list={rule.criterial_list}
                    />
                )
            default:
                return null
        }
    }

    return (
        <div className="w-full">
            <AppModalConfirmation
                title={'Eliminar regla '}
                description={
                    '¿Estás seguro de que deseas eliminar esta regla? Esta acción no se puede deshacer.'
                }
                onClose={() => setConfirmModal(false)}
                handleConfirm={() => {
                    setConfirmModal(false)
                    deleteRule(ruleToDelete)
                }}
                isOpen={confirmModal}
            />
            <Accordion
                className="bg-white w-full border-b border-b-[#CBD5E1]"
                allowZeroExpanded
                onChange={(e: string[]) => setExpandedItems(e)}
            >
                {rules.map((item, index) => (
                    <AccordionItem uuid={index.toString()}>
                        <AccordionItemHeading>
                            <AccordionItemButton className="pt-3 pr-4 pb-3 pl-3">
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center w-2/3">
                                        <span className="accordion-arrow ml-3 mr-4 transition-transform duration-300 [.accordion__button[aria-expanded='false']>&]:rotate-180">
                                            {expandedItems.includes('0') ? (
                                                <img
                                                    src={ArrowUpIcon}
                                                    alt="ArrowUpIcon"
                                                />
                                            ) : (
                                                <img
                                                    src={ArrowIcon}
                                                    alt="ArrowIcon"
                                                />
                                            )}
                                        </span>
                                        <h2 className="text-base font-normal text-[#141A1F] ">
                                            {item.name}
                                        </h2>
                                        {hasDropdownOptions && (
                                            <div
                                                className="ml-5"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                <ButtonDropdown
                                                    options={[
                                                        ...(canEdit
                                                            ? [
                                                                  {
                                                                      title: 'Editar',
                                                                      icon: IconPencil,
                                                                      onClick:
                                                                          () =>
                                                                              onEdit(
                                                                                  item.id
                                                                              ),
                                                                  },
                                                              ]
                                                            : []),
                                                        ...(canDelete
                                                            ? [
                                                                  {
                                                                      title: 'Eliminar',
                                                                      icon: IconTrash,
                                                                      onClick:
                                                                          () => {
                                                                              setConfirmModal(
                                                                                  true
                                                                              )
                                                                              setRuleToDelete(
                                                                                  item
                                                                              )
                                                                          },
                                                                  },
                                                              ]
                                                            : []),
                                                    ]}
                                                    color="primary"
                                                    imageSrc={IconDots}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <h2 className="text-base font-normal text-[#141A1F] w-1/3">
                                        {item.warehouse_name || '-'}
                                    </h2>
                                    <p className="flex items-center gap-2 w-1/3 justify-end">
                                        {sortCriteria(item.criterias).map(
                                            (rule) => {
                                                return (
                                                    <CriteriaIcon
                                                        valueKey={rule.key}
                                                    />
                                                )
                                            }
                                        )}
                                    </p>
                                </div>
                            </AccordionItemButton>
                        </AccordionItemHeading>

                        <AccordionItemPanel className="mx-3">
                            <div className="flex">
                                <div className="space-y-[10px] mx-[44px] w-2/3 mb-5">
                                    {sortCriteria(item.criterias).map(
                                        (rule) => {
                                            return renderCriteria(rule)
                                        }
                                    )}
                                </div>
                            </div>
                        </AccordionItemPanel>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    )
}
